/**
 * RFC 0345 3c: what a person lets through without being asked.
 *
 * A document a shared session's agent writes does not reach the session's
 * members until its owner approves it. A session allowance is writable by
 * every member, so no `share-edited-documents` record grants anything: "Always
 * allow" waits on the 3c trust prerequisite. Reply-to allowances (3f) require
 * a person-device signature and the sharing layer's replyAllowance verifier.
 * These generic helpers authorize nothing.
 */

import type { DocHandle } from '@automerge/automerge-repo/slim'
import type { TranscriptMessage } from '../../log'
import type { WorldDoc } from '../world/types'
import type { SessionDoc } from './types'

export interface Allowance {
  /** What it lets through without asking. */
  kind: 'share-edited-documents' | 'reply-to'
  /** 'session' lives on that session's document, where its members can see
   *  it. 'everywhere' lives in the person's own World document. */
  scope: 'session' | 'everywhere'
  sessionId?: string
  /** For reply-to: whose messages the agent answers without asking. */
  from?: string
  /** Signed reply-to records name the agent's person and their live device. */
  owner?: string
  issuer?: string
  signature?: string
}

function text(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (value !== null && typeof value === 'object') {
    const inner = (value as { val?: unknown }).val
    if (typeof inner === 'string') return inner
  }
  return undefined
}

/** Plain records, whatever wrappers the document handed back. */
function plain(list: unknown): Allowance[] {
  if (!Array.isArray(list)) return []
  // A loop, not flatMap: an Automerge list proxy in a change lacks it.
  const out: Allowance[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as Record<string, unknown>
    const kind = text(e.kind)
    const scope = text(e.scope)
    if (kind !== 'share-edited-documents' && kind !== 'reply-to') continue
    if (scope !== 'session' && scope !== 'everywhere') continue
    const sessionId = text(e.sessionId)
    const from = text(e.from)
    const owner = text(e.owner)
    const issuer = text(e.issuer)
    const signature = text(e.signature)
    out.push({
      kind,
      scope,
      ...(sessionId ? { sessionId } : {}),
      ...(from ? { from } : {}),
      ...(owner ? { owner } : {}),
      ...(issuer ? { issuer } : {}),
      ...(signature ? { signature } : {}),
    })
  }
  return out
}

const same = (a: Allowance, b: Allowance) =>
  a.kind === b.kind &&
  a.scope === b.scope &&
  a.sessionId === b.sessionId &&
  a.from === b.from

export function sessionAllowances(
  session: Pick<SessionDoc, 'allowances'> | undefined,
): Allowance[] {
  return plain(session?.allowances)
}

export function worldAllowances(
  world: Pick<WorldDoc, 'allowances'> | undefined,
): Allowance[] {
  return plain(world?.allowances)
}

/** Store an allowance where its scope says it lives. Idempotent. */
export function addAllowance(
  where: { session: DocHandle<SessionDoc>; world?: DocHandle<WorldDoc> },
  allowance: Allowance,
): void {
  if (allowance.kind === 'reply-to')
    throw new Error('Reply allowances must be signed by the agent’s person')
  const handle = allowance.scope === 'session' ? where.session : where.world
  if (!handle) throw new Error('An everywhere allowance needs the World')
  const record: Allowance =
    allowance.scope === 'session'
      ? { ...allowance, sessionId: where.session.documentId }
      : allowance
  const write = (doc: { allowances?: Allowance[] }) => {
    if (plain(doc.allowances).some((a) => same(a, record))) return
    if (!doc.allowances) doc.allowances = []
    doc.allowances.push(JSON.parse(JSON.stringify(record)))
  }
  ;(handle as DocHandle<{ allowances?: Allowance[] }>).change(write)
}

/** The bare name, whatever realm prefixed it (`mcp__mod__`, `mod.`). */
function verb(name: string): 'create' | 'update' | '' {
  const tail = name.split(/__|\./).pop() ?? name
  if (tail === 'create_document') return 'create'
  if (
    tail === 'update_document' ||
    tail === 'patch_document' ||
    tail === 'write_document'
  )
    return 'update'
  return ''
}

const CREATED = /Created document\s+(?:automerge:)?([1-9A-HJ-NP-Za-km-z]{16,})/

/**
 * Every document the session's agent wrote, oldest first. A create names its
 * document only in its result; a failed call wrote nothing; a read is not a
 * write. Ids are bare (no `automerge:`).
 */
export function documentsWrittenBySession(
  transcript: TranscriptMessage[],
): string[] {
  const results = new Map<string, { text: string; isError: boolean }>()
  for (const message of transcript) {
    if (message.role !== 'tool_result' || !message.toolUseId) continue
    results.set(String(message.toolUseId), {
      text: message.content
        .map((b) => (b.type === 'text' ? (text(b.text) ?? '') : ''))
        .join('\n'),
      isError: message.isError === true,
    })
  }
  const written: string[] = []
  for (const message of transcript) {
    if (message.role !== 'assistant') continue
    for (const block of message.content) {
      if (block.type !== 'tool_use') continue
      const kind = verb(text(block.name) ?? '')
      if (!kind) continue
      const result = results.get(String(block.id))
      // Not back yet: an edit may still fail, and a create has no id yet.
      if (!result || result.isError) continue
      const args = (block.arguments ?? {}) as Record<string, unknown>
      const id =
        kind === 'create'
          ? CREATED.exec(result.text)?.[1]
          : (text(args.documentId) ?? text(args.document_id))
      const bare = id?.replace(/^automerge:/, '')
      if (bare && !written.includes(bare)) written.push(bare)
    }
  }
  return written
}

/**
 * What the owner still has to decide on (3c): documents the agent wrote that
 * the session has not shared. With an allowance in force these are what to
 * grant without asking. `projectDocumentIds`, when given, drops what is not a
 * project document (a skill, a World record), which cannot be shared this way.
 */
export function documentsAwaitingShare(
  transcript: TranscriptMessage[],
  options: {
    sharedDocumentIds?: readonly unknown[]
    projectDocumentIds?: ReadonlySet<string>
  },
): string[] {
  const shared = new Set(
    (options.sharedDocumentIds ?? []).map((id) =>
      String(text(id) ?? id).replace(/^automerge:/, ''),
    ),
  )
  return documentsWrittenBySession(transcript).filter(
    (id) =>
      !shared.has(id) &&
      (!options.projectDocumentIds || options.projectDocumentIds.has(id)),
  )
}
